import { isUndefined, min, omit, omitBy } from 'lodash';
import type { Layout, LayoutItem } from 'react-grid-layout';

/**
 * Layout keys that `computeSourcePageEffectiveLayout` injects for rendering only. They must
 * never be persisted, and any stale value already present on a layout item (e.g. left over from
 * a block that has since been collapsed) must be dropped before recomputing.
 */
export const SYNTHETIC_LAYOUT_KEYS = ['maxH', 'static', 'isDraggable', 'isResizable'] as const;

const hasSyntheticKey = (layout: LayoutItem): boolean => SYNTHETIC_LAYOUT_KEYS.some((key) => key in layout);

const nearestExpandedTopAtOrBelow = (expandedTops: number[], itemBottom: number): number | undefined =>
  min(expandedTops.filter((top) => top >= itemBottom));

/**
 * Derives the RGL layout actually rendered for a source page: expanded blocks are pinned to
 * their expanded row count and locked (not draggable/resizable), and any block above an expanded
 * block gets `maxH` clamped to the expanded block's top edge so resizing it can't push content
 * past the footer. Stale synthetic keys are stripped before recomputing so a value left over from
 * a since-collapsed block never leaks into the freshly derived layout.
 */
export const computeSourcePageEffectiveLayout = ({
  pageLayout,
  expandedStates,
  expandedRows,
}: {
  pageLayout: Layout;
  expandedStates: boolean[];
  expandedRows: number[];
}): Layout => {
  const anyExpanded = expandedStates.some(Boolean);
  if (!anyExpanded && !pageLayout.some(hasSyntheticKey)) return pageLayout;

  const expandedTops = pageLayout.filter((_, idx) => expandedStates[idx]).map((layout) => layout.y);

  return pageLayout.map((layout, idx) => {
    if (expandedStates[idx]) {
      return {
        ...omit(layout, SYNTHETIC_LAYOUT_KEYS),
        h: expandedRows[idx] ?? layout.h,
        static: true,
        isDraggable: false,
      };
    }

    const boundaryTop = nearestExpandedTopAtOrBelow(expandedTops, layout.y + layout.h);
    if (boundaryTop === undefined) {
      return hasSyntheticKey(layout) ? (omit(layout, SYNTHETIC_LAYOUT_KEYS) as LayoutItem) : layout;
    }
    return { ...omit(layout, SYNTHETIC_LAYOUT_KEYS), maxH: boundaryTop - layout.y };
  });
};

/**
 * Sanitizes the layout RGL reports back through `onLayoutChange` before it is persisted:
 * synthetic keys are stripped, and expanded items are mapped back to their persisted entry by
 * `i` rather than by index, since the RGL fork's callback returns a compacted and reordered
 * array that no longer lines up positionally with the persisted layout.
 */
export const sanitizeSourcePageLayout = ({
  givenLayout,
  persistedById,
  expandedIds,
}: {
  givenLayout: Layout;
  persistedById: ReadonlyMap<string, LayoutItem>;
  expandedIds: ReadonlySet<string>;
}): Layout => {
  return givenLayout.map((layout) => {
    const persisted = persistedById.get(layout.i);
    if (expandedIds.has(layout.i) && persisted) return persisted;
    return omitBy(
      layout,
      (value, key) => isUndefined(value) || (SYNTHETIC_LAYOUT_KEYS as readonly string[]).includes(key),
    ) as LayoutItem;
  });
};

export const sortLayoutByReadingOrder = (layout: Layout): LayoutItem[] =>
  [...layout].sort((a, b) => a.y - b.y || a.x - b.x);

export const packLayoutByReadingOrder = ({ items, noColumns }: { items: Layout; noColumns: number }): LayoutItem[] => {
  const result: LayoutItem[] = [];
  let cursorX = 0;
  let rowTopY = 0;
  let rowMaxH = 0;

  for (const item of items) {
    const w = Math.min(item.w, noColumns);
    if (cursorX > 0 && cursorX + w > noColumns) {
      rowTopY += rowMaxH;
      cursorX = 0;
      rowMaxH = 0;
    }
    result.push({ ...item, x: cursorX, y: rowTopY, w });
    cursorX += w;
    rowMaxH = Math.max(rowMaxH, item.h);
    if (cursorX >= noColumns) {
      rowTopY += rowMaxH;
      cursorX = 0;
      rowMaxH = 0;
    }
  }

  return result;
};

export const reorderBlockWithinPage = ({
  layout,
  fromIndex,
  toIndex,
  noColumns,
}: {
  layout: Layout;
  fromIndex: number;
  toIndex: number;
  noColumns: number;
}): LayoutItem[] => {
  const sorted = sortLayoutByReadingOrder(layout);
  const [moved] = sorted.splice(fromIndex, 1);
  if (moved) {
    sorted.splice(toIndex, 0, moved);
  }
  return packLayoutByReadingOrder({ items: sorted, noColumns });
};

export const insertBlockIntoPage = ({
  layout,
  item,
  atIndex,
  noColumns,
}: {
  layout: Layout;
  item: LayoutItem;
  atIndex: number;
  noColumns: number;
}): LayoutItem[] => {
  const sorted = sortLayoutByReadingOrder(layout);
  const clampedIndex = Math.min(Math.max(atIndex, 0), sorted.length);
  sorted.splice(clampedIndex, 0, item);
  return packLayoutByReadingOrder({ items: sorted, noColumns });
};
